import { Icon } from "@elit-hair/elit-ui";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { TbActivity } from "react-icons/tb";

import { PAGINATION } from "@/constants/pagination";
import { useGetIncidents } from "@/queries/incidents.queries";
import { INCIDENT_REPORT_PRIORITY } from "@/types/incident.types";

import { IncidentStatusBadge } from "./incident-status-badge";

const PRIORITY_DOT: Record<INCIDENT_REPORT_PRIORITY, string> = {
    [INCIDENT_REPORT_PRIORITY.LOW]: "bg-emerald-500",
    [INCIDENT_REPORT_PRIORITY.MEDIUM]: "bg-yellow-500",
    [INCIDENT_REPORT_PRIORITY.HIGH]: "bg-red-500",
};

type TrendingIncidentsProps = {
    enabled: boolean;
};

export function TrendingIncidents({ enabled }: TrendingIncidentsProps) {
    const { t } = useTranslation();
    const { data, isLoading } = useGetIncidents({ page: PAGINATION.DEFAULT_PAGE, limit: 5 }, { enabled });
    const incidents = data?.data ?? [];

    return (
        <div className="min-w-0 overflow-hidden rounded-md border border-border/60 bg-muted/30">
            {/* Header */}
            <div className="flex items-center gap-2 border-b border-border/60 px-3 py-2">
                <Icon icon={TbActivity} size={14} className="shrink-0 text-muted-foreground" />
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("incident_trending_title")}
                </span>
                {!isLoading && incidents.length > 0 && (
                    <span className="ml-auto rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground">
                        {data?.pagination.total ?? incidents.length}
                    </span>
                )}
            </div>

            {/* Content */}
            <div className="max-h-[180px] overflow-y-auto overflow-x-hidden">
                {isLoading ? (
                    <div className="space-y-px px-1 py-1">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="flex items-center gap-2 rounded px-2 py-2">
                                <div className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-muted-foreground/30" />
                                <div className="h-3 flex-1 animate-pulse rounded bg-muted-foreground/20" />
                                <div className="h-3 w-14 animate-pulse rounded bg-muted-foreground/20" />
                            </div>
                        ))}
                    </div>
                ) : incidents.length === 0 ? (
                    <p className="px-3 py-3 text-center text-xs text-muted-foreground">
                        {t("incident_trending_empty")}
                    </p>
                ) : (
                    <ul className="space-y-px px-1 py-1">
                        {incidents.map((incident) => {
                            const ago = DateTime.fromISO(incident.createdAt).toRelative();
                            return (
                                <li
                                    key={incident.id}
                                    className="flex min-w-0 items-start gap-2 rounded px-2 py-1.5 transition-colors hover:bg-muted/60"
                                >
                                    {/* Priority dot */}
                                    <span
                                        className={`mt-1 h-2 w-2 shrink-0 rounded-full ${PRIORITY_DOT[incident.priority]}`}
                                        title={t(`incident_priority_${incident.priority.toLowerCase()}`)}
                                    />

                                    {/* Description – wraps up to 2 lines for long reports */}
                                    <span
                                        className="line-clamp-2 min-w-0 flex-1 wrap-break-word text-xs leading-snug text-foreground/90"
                                        title={incident.description}
                                    >
                                        {incident.description}
                                    </span>

                                    {/* Meta (status + time) stays compact on the right */}
                                    <span className="flex shrink-0 items-center gap-1.5">
                                        <IncidentStatusBadge status={incident.status} size="compact" />
                                        <span className="whitespace-nowrap text-[10px] tabular-nums text-muted-foreground">
                                            {ago}
                                        </span>
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
